import { fontAttrs } from "@/components/tools/printables/page-kit";
import { r2 } from "@/lib/tool-kit/printables/page";
import type {
  BehaviorLayout,
  HeaderLayout,
  PlacedText,
  RewardShape,
  ShapeMark,
  StickerLayout,
  TokenBoard,
} from "@/lib/tool-kit/printables/reward-chart";
import { heartPath, starPath } from "@/lib/tool-kit/printables/shapes";

const INK = "#1a1a1a";
const MUTED = "#8a8a8a";
const BOLD = { kind: "sans", bold: true } as const;
const SANS = { kind: "sans" } as const;

function Label({ item, fill = INK }: { item: PlacedText; fill?: string }) {
  return (
    <text
      fill={fill}
      fontSize={item.size}
      textAnchor={item.anchor}
      x={item.x}
      y={item.y}
      {...fontAttrs(item.bold ? BOLD : SANS)}
    >
      {item.text}
    </text>
  );
}

/** One outlined shape with an optional faint number inside. */
export function ShapeGlyph({
  shape,
  mark,
  accent,
  dashed = false,
}: {
  shape: RewardShape;
  mark: ShapeMark;
  accent: string;
  dashed?: boolean;
}) {
  const common = {
    fill: "none",
    stroke: accent,
    strokeWidth: mark.final ? 0.9 : 0.5,
    strokeDasharray: dashed ? "1.4 1" : undefined,
  } as const;
  const half = mark.size / 2;
  let body: React.ReactNode;
  if (shape === "circle") {
    body = <circle cx={mark.cx} cy={mark.cy} r={r2(half)} {...common} />;
  } else if (shape === "square") {
    body = (
      <rect
        height={mark.size}
        rx={r2(mark.size * 0.12)}
        width={mark.size}
        x={r2(mark.cx - half)}
        y={r2(mark.cy - half)}
        {...common}
      />
    );
  } else if (shape === "star") {
    body = (
      <path
        d={starPath(mark.cx, mark.cy, half * 1.08, half * 0.44)}
        strokeLinejoin="round"
        {...common}
      />
    );
  } else {
    body = (
      <path
        d={heartPath(mark.cx, mark.cy, mark.size * 1.02)}
        strokeLinejoin="round"
        {...common}
      />
    );
  }
  return (
    <g>
      {body}
      {mark.number ? (
        <text
          fill={MUTED}
          fontSize={r2(Math.min(5, mark.size * 0.26))}
          textAnchor="middle"
          x={mark.cx}
          y={r2(mark.cy + mark.size * 0.09)}
          {...fontAttrs(SANS)}
        >
          {mark.number}
        </text>
      ) : null}
    </g>
  );
}

export function ChartHeader({
  header,
  accent,
}: {
  header: HeaderLayout;
  accent: string;
}) {
  return (
    <g>
      {header.title.map((line) => (
        <Label fill={accent} item={line} key={line.y} />
      ))}
      {header.name ? (
        <g>
          <Label fill="#444444" item={header.name.label} />
          <line
            stroke={INK}
            strokeWidth={0.3}
            x1={header.name.rule.x1}
            x2={header.name.rule.x2}
            y1={header.name.rule.y}
            y2={header.name.rule.y}
          />
        </g>
      ) : null}
      {header.goal ? (
        <g>
          <Label fill="#444444" item={header.goal.label} />
          <line
            stroke={INK}
            strokeWidth={0.3}
            x1={header.goal.rule.x1}
            x2={header.goal.rule.x2}
            y1={header.goal.rule.y}
            y2={header.goal.rule.y}
          />
          {header.goal.value ? <Label item={header.goal.value} /> : null}
        </g>
      ) : null}
    </g>
  );
}

/** The sticker chart page: header and the numbered grid. */
export function StickerArt({
  layout,
  shape,
  accent,
  finishLabel,
}: {
  layout: StickerLayout;
  shape: RewardShape;
  accent: string;
  finishLabel: string;
}) {
  const last = layout.marks.at(-1);
  return (
    <g>
      <ChartHeader accent={accent} header={layout.header} />
      {layout.marks.map((mark) => (
        <ShapeGlyph
          accent={accent}
          key={`${mark.cx}-${mark.cy}`}
          mark={mark}
          shape={shape}
        />
      ))}
      {last && finishLabel ? (
        <text
          fill={accent}
          fontSize={r2(Math.min(4.2, last.size * 0.22))}
          textAnchor="middle"
          x={last.cx}
          y={r2(
            last.cy + last.size / 2 + Math.min(4.2, last.size * 0.22) + 1.2
          )}
          {...fontAttrs(BOLD)}
        >
          {finishLabel}
        </text>
      ) : null}
    </g>
  );
}

/** The weekly behaviour chart: goals, days and a shape in every cell. */
export function BehaviorArt({
  layout,
  shape,
  accent,
}: {
  layout: BehaviorLayout;
  shape: RewardShape;
  accent: string;
}) {
  const { table } = layout;
  const bottom = table.y + table.height;
  return (
    <g>
      <ChartHeader accent={accent} header={layout.header} />
      <rect
        fill={accent}
        height={10}
        opacity={0.12}
        width={table.width}
        x={table.x}
        y={table.y}
      />
      {layout.days.map((day) => (
        <Label item={day.text} key={day.x} />
      ))}
      {layout.total ? <Label item={layout.total.head} /> : null}
      {layout.goals.map((goal) => (
        <g key={goal.y}>
          {goal.label.map((line) => (
            <Label item={line} key={line.y} />
          ))}
        </g>
      ))}
      {layout.marks.map((mark) => (
        <ShapeGlyph
          accent={accent}
          key={`${mark.cx}-${mark.cy}`}
          mark={mark}
          shape={shape}
        />
      ))}
      <rect
        fill="none"
        height={table.height}
        stroke={INK}
        strokeWidth={0.4}
        width={table.width}
        x={table.x}
        y={table.y}
      />
      {layout.rules.map((y) => (
        <line
          key={y}
          stroke={MUTED}
          strokeWidth={0.3}
          x1={table.x}
          x2={r2(table.x + table.width)}
          y1={y}
          y2={y}
        />
      ))}
      {layout.columns.slice(1, -1).map((x) => (
        <line
          key={x}
          stroke={MUTED}
          strokeWidth={0.3}
          x1={x}
          x2={x}
          y1={table.y}
          y2={r2(bottom)}
        />
      ))}
      {layout.total ? (
        <line
          stroke={MUTED}
          strokeWidth={0.3}
          x1={layout.total.x}
          x2={layout.total.x}
          y1={table.y}
          y2={r2(bottom)}
        />
      ) : null}
      {layout.footer ? (
        <g>
          <Label fill="#444444" item={layout.footer.rewardLine} />
          <line
            stroke={INK}
            strokeWidth={0.3}
            x1={layout.footer.rule.x1}
            x2={layout.footer.rule.x2}
            y1={layout.footer.rule.y}
            y2={layout.footer.rule.y}
          />
        </g>
      ) : null}
    </g>
  );
}

/** Token boards: a title, a row of tokens and a reward box, with cut lines between boards. */
export function TokenArt({
  boards,
  shape,
  accent,
}: {
  boards: readonly TokenBoard[];
  shape: RewardShape;
  accent: string;
}) {
  return (
    <g>
      {boards.map((board, index) => (
        <g key={board.box.y}>
          {board.title ? <Label fill={accent} item={board.title} /> : null}
          {board.marks.map((mark) => (
            <ShapeGlyph
              accent={accent}
              key={`${mark.cx}-${mark.cy}`}
              mark={mark}
              shape={shape}
            />
          ))}
          <rect
            fill="none"
            height={board.reward.rect.height}
            rx={3}
            stroke={accent}
            strokeWidth={0.7}
            width={board.reward.rect.width}
            x={board.reward.rect.x}
            y={board.reward.rect.y}
          />
          <Label fill="#444444" item={board.reward.label} />
          {board.reward.value.map((line) => (
            <Label item={line} key={line.y} />
          ))}
          {index > 0 ? (
            <line
              stroke="#9a9a9a"
              strokeDasharray="2 1.5"
              strokeWidth={0.25}
              x1={board.box.x}
              x2={r2(board.box.x + board.box.width)}
              y1={board.box.y}
              y2={board.box.y}
            />
          ) : null}
        </g>
      ))}
    </g>
  );
}
